import { Modal } from 'antd';
import React, { FC, useState } from 'react';
import styles from './findLocationModal.module.scss';
import { Form, Formik } from 'formik';
import clsx from 'clsx';
import Map from '../map';
import InputField from '../InputField';
import searchplaceByGeoCode from 'shared/utils/searchPlacByGeocode';
import { validationSchema } from './validationSchema';
import COButton from '../COButton';
import { Address } from 'models/Address/address.model';
import fetchAddressFromGeoCode from 'shared/utils/fetchAddressFromGeoCode';
import { modalCloseIcon } from '../OrganizationModal';

interface FindLocationModalProps {
  showModal: boolean;
  onSave: (data: Address) => void;
  onCancel: () => void;
  initialData?: Address | undefined;
  fetchInitialData?: () => Address | undefined;
}

const FindLocationModal: FC<FindLocationModalProps> = props => {
  const { showModal, onSave, onCancel, initialData, fetchInitialData } = props;

  const searchPlaceByCountryProvince = async (
    country: string,
    province: string,
    setFieldValue: Function
  ) => {
    try {
      setFetchingAddress(true);
      const results: google.maps.GeocoderResult[] | undefined =
        await searchplaceByGeoCode(`${province} ${country}`);
      if (results && results!.length > 0 && results![0]) {
        const latling = results![0].geometry.location;
        const addressComponents = results![0].address_components;
        const formattedAddress = results![0].formatted_address;
        setFieldValue('map', formattedAddress);
        setAddressInState(addressComponents, latling, setFieldValue);
      }
    } catch (error) {
      console.error('error is ', error);
    } finally {
      setFetchingAddress(false);
    }
  };

  const setAddressInState = (
    addressComponents: google.maps.GeocoderAddressComponent[],
    latling: google.maps.LatLng | null,
    setFieldValue: Function
  ) => {
    const { streetLocality, country, province, city, pinCode } =
      fetchAddressFromGeoCode(addressComponents);

    if (streetLocality.trim() === '') {
      setFieldValue('street', '');
    } else {
      setFieldValue('street', streetLocality?.trim() ?? '');
    }

    setFieldValue('country', country?.long_name ?? '');
    setFieldValue('country_code', country?.short_name ?? '');
    setFieldValue('province', province?.long_name ?? '');
    setFieldValue('city', city?.long_name ?? '');
    setFieldValue('postal_code', pinCode?.long_name ?? '');
    setFieldValue('latitude', latling?.lat());
    setFieldValue('longitude', latling?.lng());
  };

  const data: Address | undefined = fetchInitialData
    ? fetchInitialData()
    : undefined;

  const initialValues = {
    city: '',
    street: '',
    country: '',
    province: '',
    postal_code: initialData?.postalCode ?? '',
    latitude: '',
    longitude: '',
    country_code: '',
    ...initialData,
    ...data,
  };

  const [fetchingAddress, setFetchingAddress] = useState<boolean>(false);

  return (
    <Modal
      destroyOnClose={true}
      maskClosable={false}
      width={725}
      footer={null}
      wrapClassName={styles.modalContainer}
      open={showModal}
      closeIcon={modalCloseIcon}
      onCancel={onCancel}>
      <div className={styles.formContainer}>
        <div className={styles.headerContainer}>
          <h2>Find Location</h2>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={data => onSave(data)}
            validationSchema={validationSchema}>
            {({ setFieldValue, isSubmitting, values }) => (
              <Form>
                <div className={styles.fieldContainer}>
                  <div
                    className={clsx({
                      [styles.inputContainer]: true,
                      [styles.longContainer]: true,
                    })}>
                    <div className={styles.mapContainer}>
                      <Map
                        name="map"
                        setAddress={(
                          addressComponents: google.maps.GeocoderAddressComponent[],
                          latling: google.maps.LatLng | null
                        ) =>
                          setAddressInState(
                            addressComponents,
                            latling,
                            setFieldValue
                          )
                        }
                        latitude={values?.latitude}
                        longitude={values?.longitude}
                        focusSearchInputOnReady={showModal}
                      />
                    </div>
                  </div>
                  <div className={styles.inputContainer}>
                    <InputField
                      name="street"
                      placeholderstring="Street, Locality"
                      label=""
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <InputField name="city" placeholderstring="City" label="" />
                  </div>
                  <div className={styles.inputContainer}>
                    <InputField
                      name="province"
                      placeholderstring="Province"
                      label=""
                      onBlur={() =>
                        searchPlaceByCountryProvince(
                          values.country ?? '',
                          values.province ?? '',
                          setFieldValue
                        )
                      }
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <InputField
                      name="country"
                      placeholderstring="Country"
                      label=""
                      onBlur={() =>
                        searchPlaceByCountryProvince(
                          values.country ?? '',
                          values.province ?? '',
                          setFieldValue
                        )
                      }
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <InputField
                      name="postal_code"
                      placeholderstring="Postal Code"
                      label=""
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <InputField
                      name="postal_codes"
                      placeholderstring="Postal Code"
                      label=""
                      className="d-none"
                    />
                  </div>
                </div>

                <div className={styles.buttonContainer}>
                  <div className={styles.singleButtonContainer}>
                    <COButton submit loading={fetchingAddress}>
                      Done
                    </COButton>
                    <COButton secondary onClick={onCancel}>
                      Cancel
                    </COButton>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </Modal>
  );
};

export default FindLocationModal;
